import { iconDoc2aVia, iconNavPasta } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsIcon, BsdsInput, BsdsModal } from '@bsds-lib-component/react';
import React, { useId, useState, useEffect } from 'react';

import { SystemMessage, useSystemContainer, Visibility, Dropdown, Pagination, Radio } from '../../../components';
import Button from '../../../components/button/Button';
import DownloadAdhesionTable from '../../../components/table/previdencia/DownloadAdhesionTable';
import { usePrevidenciaAdhesion } from '../../../hooks';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import { apiService } from '../../../services';
import type {
  AdhesionListRequest,
  AdhesionListResponseDTO,
  DownloadAdhesionDetailRequestDTO,
  SubCompanyResponseDTO,
} from '../../../types/previdencia/previdenciaType';
import type { AdhesionTableItem } from '../../../types/previdencia/table/adhesionTableType';

// Helper function to format date from yyyy-MM-dd to dd/MM/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

// Helper function to map adhesion status
const mapStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    APROVADO: 'Aprovado',
    RECUSADO: 'Recusado',
    PENDENTE: 'Pendente',
    CANCELADO: 'Cancelado',
  };
  return statusMap[status] || status;
};

const DownloadAdhesionTab: React.FC = () => {

  const companyId = '11';

  const defaultSubCompanyId = '3';

  const {
    adhesions,
    adhesionDetail,
    isLoading,
    clearError,
    listAdhesions,
    getAdhesionDetail,
    downloadAdhesionDetail,
    clearAdhesionDetail,
    totalPages,
    totalElements,
    currentPage,
  } = usePrevidenciaAdhesion();
  const { showMessage, message, type, show } = useSystemMessage();
  const { setLoading } = useSystemContainer();
  const mainContentId = useId();
  const searchFormId = useId();
  const resultsId = useId();
  const emptyStateId = useId();
  const messageId = useId();
  const modalId = useId();

  // Form states
  const [subCompanyId, setSubCompanyId] = useState(defaultSubCompanyId);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('AMBOS'); // Default to 'AMBOS' for radio selection
  const [subCompanies, setSubCompanies] = useState<SubCompanyResponseDTO[]>([]);
  const [isLoadingSubCompanies, setIsLoadingSubCompanies] = useState(false);

  // Store current search params for pagination
  const [currentSearchParams, setCurrentSearchParams] = useState<AdhesionListRequest | null>(null);

  // State to control if there are search results
  const [hasSearchResults, setHasSearchResults] = useState<boolean>(false);
  // State to track if a search was performed
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  // State to control modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Status options
  const statusOptions = [
    { label: 'Aprovado', value: 'APROVADO' },
    { label: 'Recusado', value: 'RECUSADO' },
    { label: 'Ambos', value: 'AMBOS' },
  ];

  // Load subcompanies on component mount
  useEffect(() => {
    const loadSubCompanies = async () => {
      if (!companyId) {
        setSubCompanies([]);
        return;
      }

      setIsLoadingSubCompanies(true);
      try {
        const subCompaniesList = await apiService.subCompany.getSubCompanies(companyId);
        setSubCompanies(subCompaniesList);

        if (subCompaniesList.length > 0 && !subCompanyId) {
          setSubCompanyId(subCompaniesList[0].id.toString());
        } else if (subCompaniesList.length === 0 && !subCompanyId) {
          setSubCompanyId(defaultSubCompanyId);
        }
      } catch {
        showMessage('Não foi possível carregar as sucursais', 'warning');
        setSubCompanies([]);
      } finally {
        setIsLoadingSubCompanies(false);
      }
    };

    loadSubCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  const hasRequiredFields = companyId && (subCompanyId || subCompanies.length > 0 || companyId);

  // Validate dates: both must be filled and dateFrom must be less than dateTo
  const areDatesValid = dateFrom && dateTo && new Date(dateFrom) < new Date(dateTo);

  const isSearchDisabled =
    isLoading ||
    isLoadingSubCompanies ||
    !hasRequiredFields ||
    !areDatesValid;

  const handleSearch = async () => {
    if (!companyId) {
      showMessage('Por favor, selecione uma empresa', 'warning');
      return;
    }

    const selectedSubCompanyId = subCompanyId ||
      (subCompanies.length > 0 ? subCompanies[0].id.toString() : defaultSubCompanyId);

    if (!selectedSubCompanyId) {
      showMessage('Por favor, selecione uma sucursal', 'warning');
      return;
    }

    // Resets states before performing new search
    clearError();
    setHasSearchResults(false);
    setHasSearched(false);
    clearAdhesionDetail();

    try {
      const searchParams: AdhesionListRequest = {
        companyId,
        subCompanyId: selectedSubCompanyId,
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(status && status !== '' && status !== 'AMBOS' && { status }),
        page: 0,
        size: 6,
      };

      setCurrentSearchParams(searchParams);
      await listAdhesions(searchParams);
      setHasSearched(true);
    } catch (err) {
      console.error('Erro ao buscar adesões:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao buscar adesões';

      // Não mostra erro se for "não encontrado" - isso é tratado como resultado vazio
      const isNotFound = errorMsg.toLowerCase().includes('não encontrado') ||
        errorMsg.toLowerCase().includes('nao encontrado') ||
        errorMsg.toLowerCase().includes('not found');

      if (!isNotFound) {
        showMessage(errorMsg, 'error');
      }
      setHasSearchResults(false);
      setHasSearched(true);
    }
  };


  const handlePageChange = async (page: number) => {
    if (!currentSearchParams) {
      return;
    }

    try {
      const searchParams: AdhesionListRequest = {
        ...currentSearchParams,
        page: page - 1, // API uses 0-based, component uses 1-based
      };

      await listAdhesions(searchParams);
      setCurrentSearchParams(searchParams);
    } catch (err) {
      console.error('Erro ao mudar de página:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao mudar de página';
      showMessage(errorMsg, 'error');
    }
  };

  const handleInputChange = (setter: (value: string) => void) => (event: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = event.target as HTMLInputElement;
    setter(target?.value || '');
  };

  const handleRowClick = async (adhesion: AdhesionTableItem | AdhesionListResponseDTO) => {
    try {
      await getAdhesionDetail(adhesion.adhesionId);
      setIsModalOpen(true);
    } catch (err) {
      console.error('Erro ao carregar detalhes da adesão:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar detalhes da adesão';
      showMessage(errorMsg, 'error');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    clearAdhesionDetail();
  };

  const handleDownload = async (adhesion: AdhesionTableItem | AdhesionListResponseDTO) => {
    try {
      const request: DownloadAdhesionDetailRequestDTO = {
        adhesionProposalId: adhesion.adhesionId,
      };
      await downloadAdhesionDetail(request);
      showMessage('Download iniciado com sucesso', 'success');
    } catch (err) {
      console.error('Erro ao fazer download da adesão:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao fazer download da adesão';
      showMessage(errorMsg, 'error');
    }
  };

  // Subcompany options for dropdown
  const subCompanyOptions = subCompanies.map((sc) => ({
    label: sc.name,
    value: sc.id.toString(),
  }));

  React.useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  // Sync hasSearchResults with adhesions after search and show success message
  React.useEffect(() => {
    if (hasSearched && !isLoading) {
      const hasResults = adhesions.length > 0;
      setHasSearchResults(hasResults);

      // Show success message only when we have results
      if (hasResults) {
        showMessage('Adesões encontradas com sucesso', 'success');
      }
    }
  }, [adhesions, hasSearched, isLoading, showMessage]);

  return (
    <div
      className="bsds-width-100 bsds-mt-12 bsds-p-8"
      role="main"
      aria-busy={isLoading}
      aria-label="Download de adesões"
      id={mainContentId}
    >
      {show && (
        <div
          id={messageId}
          className="bsds-mb-16"
          role={type === 'error' ? 'alert' : 'status'}
          aria-live={type === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <SystemMessage message={message} type={type} show={show} />
        </div>
      )}

      {/* Header */}
      <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
        <BsdsIcon icon={iconDoc2aVia} className="w-4 h-4" aria-hidden="true" />
        <h6 className="bsds-ml-8 font-semibold">Download de Adesões</h6>
      </div>

      {/* Search Form */}
      <div
        id={searchFormId}
        className="bsds-mb-24 bsds-mt-24"
        role="region"
        aria-label="Formulário de busca de adesões"
      >
        <div className="bsds-mb-16">
          {/* Sucursal - Full width row */}
          <div className="bsds-mb-16">
            <Dropdown
              label="Sucursal"
              placeholder={
                isLoadingSubCompanies
                  ? 'Carregando sucursais...'
                  : subCompanies.length === 0
                    ? 'Nenhuma sucursal disponível'
                    : 'Selecione a sucursal'
              }
              value={subCompanyId}
              onChange={setSubCompanyId}
              options={subCompanyOptions}
              disabled={!companyId || isLoadingSubCompanies || subCompanies.length === 0}
              required
            />
          </div>

          <span className="bsds-text-sm text-text-color">Período (Limite máximo de 62 dias)</span>

          {/* Dates row - Data Inicial and Data Final */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bsds-mb-16 bsds-mt-8">
            {/* Date From */}
            <BsdsInput
              value={dateFrom}
              onInput={handleInputChange(setDateFrom)}
              label="Data Inicial"
              placeholder="yyyy-MM-dd"
              type="date"
              color="primary"
              platform="web"
              className="bsds-width-100"
            />

            {/* Date To */}
            <BsdsInput
              value={dateTo}
              onInput={handleInputChange(setDateTo)}
              label="Data Final"
              placeholder="yyyy-MM-dd"
              type="date"
              color="primary"
              platform="web"
              className="bsds-width-100"
            />
          </div>

          {/* Buttons */}
          <div
            className="bsds-display-flex bsds-justify-content-end gap-4"
            role="group"
            aria-label="Ações do formulário de busca"
          >

            <Button
              onClick={handleSearch}
              color="primary"
              fill="solid"
              loading={isLoading}
              disabled={isSearchDisabled}
              icon={iconNavPasta}
              iconPosition="start"
              aria-label={
                isLoading
                  ? 'Pesquisando adesões...'
                  : isSearchDisabled
                    ? !companyId
                      ? 'Selecione uma empresa para pesquisar'
                      : subCompanies.length === 0
                        ? 'Nenhuma sucursal disponível. Não é possível pesquisar sem uma sucursal.'
                        : !subCompanyId
                          ? 'Selecione uma sucursal para pesquisar'
                          : !dateFrom || !dateTo
                            ? 'Preencha a data inicial e a data final para gerar o arquivo'
                            : !areDatesValid
                              ? 'A data inicial deve ser menor que a data final'
                              : 'Preencha os campos obrigatórios para pesquisar'
                    : 'Pesquisar adesões'
              }
            >
              Gerar Arquivo
            </Button>
          </div>

          {/* Status */}
          <Visibility isActive={hasSearchResults && adhesions.length > 0} animated animationDuration={300}>
            <div className="bsds-mb-16">
              <label className="bsds-display-block bsds-text-sm font-semibold bsds-mb-8 text-text-color" htmlFor="status-group">
                Status
              </label>
              <div
                id="status-group"
                className="bsds-display-flex bsds-align-items-center gap-6"
                role="radiogroup"
                aria-label="Selecione o status da adesão"
              >
                {statusOptions.map((option) => (
                  <div
                    key={option.value}
                    className="bsds-display-flex bsds-align-items-center gap-2 cursor-pointer"
                    onClick={() => setStatus(option.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setStatus(option.value);
                      }
                    }}
                    role="radio"
                    aria-checked={status === option.value}
                    tabIndex={0}
                    aria-label={option.label}
                  >
                    <Radio
                      checked={status === option.value}
                      onChange={() => setStatus(option.value)}
                      aria-label={option.label}
                    />
                    <span className="bsds-text-sm text-text-color">{option.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </Visibility>
        </div>
      </div>

      {/* Results content - visible only when there is data */}
      <Visibility isActive={hasSearchResults && adhesions.length > 0} animated animationDuration={300}>
        <div
          id={resultsId}
          className="bsds-mb-16 bsds-mt-34"
          role="region"
          aria-label="Resultados da busca de adesões"
        >
          <BsdsCard className="bsds-p-16">
            <DownloadAdhesionTable
              adhesions={adhesions.map((adhesion): AdhesionTableItem => ({
                ...adhesion,
                id: adhesion.adhesionId,
              }))}
              onRowClick={handleRowClick}
              onDownload={handleDownload}
              isLoading={isLoading}
              emptyMessage="Nenhuma adesão encontrada"
            />
          </BsdsCard>
          {!isLoading && totalElements > 0 && (
            <div className="bsds-mt-16 bsds-mb-8">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={totalElements}
                itemLabel="adesões"
                alwaysShow
              />
            </div>
          )}
        </div>
      </Visibility>

      <Visibility isActive={hasSearched && !hasSearchResults} animated animationDuration={300}>
        <div
          id={emptyStateId}
          className="bsds-display-flex bsds-justify-content-center bsds-align-items-center bsds-mt-64 bsds-mb-64"
          role="status"
          aria-live="polite"
          aria-label="Resultado da busca"
        >
          <div className="bsds-text-center">
            <p className="bsds-text-regular bsds-text-lg text-gray-600">
              Dados não encontrados para a pesquisa realizada
            </p>
          </div>
        </div>
      </Visibility>

      {/* Detail Modal */}
      <BsdsModal
        isOpen={isModalOpen}
        onBsdsModalDidDismiss={handleCloseModal}
        backdropDismiss
        color="secondary"
        canDismiss
        animated
        showBackdrop
        keepContentsMounted
        platform="web"
        aria-labelledby={modalId}
        aria-describedby={`${modalId}-description`}
      >
        <div
          className="modal-content flex flex-col h-screen max-h-screen md:h-[75vh]"
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalId}
        >
          {adhesionDetail && (
            <>
              {/* Header fixed */}
              <div className="flex items-center gap-4 p-4 pb-3 flex-shrink-0 border-b border-gray-100">
                <BsdsIcon icon={iconDoc2aVia} color="secondary" className="icon-title" aria-hidden="true" />
                <h6 id={modalId} className="text-lg font-semibold text-text-color">
                  Detalhes da Adesão
                </h6>
              </div>

              {/* Scroll content */}
              <div
                id={`${modalId}-description`}
                className="overflow-y-auto custom-scrollbar flex-1 px-4 py-4 min-h-0 pb-38"
                role="region"
                aria-label="Detalhes da adesão"
              >
                <div className="flex flex-col gap-4">
                  {/* Contract Info */}
                  <BsdsCard className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-p-16 bg-gray-100/30">
                    <div>
                      <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Número do Contrato</span>
                      <span className="bsds-text-regular font-semibold">
                        {adhesionDetail.contractNumber || '-'}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Empresa</span>
                      <span className="bsds-text-regular">{adhesionDetail.companyTradeName || '-'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Status</span>
                      <span className="bsds-text-regular">{mapStatus(adhesionDetail.adhesionCurrentStatus)}</span>
                    </div>
                  </BsdsCard>

                  {/* Adhesion Data */}
                  <BsdsCard className="bsds-p-16" role="region" aria-label="Dados da adesão">
                    <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                      <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
                      <h4 className="opacity-80">Dados da Adesão</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">ID da Adesão</span>
                        <span className="bsds-text-regular">{adhesionDetail.adhesionId || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Status</span>
                        <span className="bsds-text-regular">{mapStatus(adhesionDetail.adhesionCurrentStatus) || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">CPF do Proponente</span>
                        <span className="bsds-text-regular">{adhesionDetail.adhesionProposerCpf || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Nome do Proponente</span>
                        <span className="bsds-text-regular">{adhesionDetail.adhesionProposerName || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Prazo DEC</span>
                        <span className="bsds-text-regular">{adhesionDetail.adhesionPrazoDec || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Origem de Importação</span>
                        <span className="bsds-text-regular">{adhesionDetail.adhesionImportSource || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Data do Status</span>
                        <span className="bsds-text-regular">
                          {adhesionDetail.adhesionCurrentStatusAt
                            ? new Date(adhesionDetail.adhesionCurrentStatusAt).toLocaleString('pt-BR')
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </BsdsCard>

                  {/* Company Info */}
                  <BsdsCard className="bsds-p-16" role="region" aria-label="Informações da empresa">
                    <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                      <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
                      <h4 className="opacity-80">Informações da Empresa</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">CNPJ</span>
                        <span className="bsds-text-regular">{adhesionDetail.companyCnpj || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Razão Social</span>
                        <span className="bsds-text-regular">{adhesionDetail.companyCorporateName || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Nome Fantasia</span>
                        <span className="bsds-text-regular">{adhesionDetail.companyTradeName || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Sucursal</span>
                        <span className="bsds-text-regular">{adhesionDetail.subCompanyName || '-'}</span>
                      </div>
                    </div>
                  </BsdsCard>

                  {/* Contract Info */}
                  <BsdsCard className="bsds-p-16" role="region" aria-label="Informações do contrato">
                    <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                      <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
                      <h4 className="opacity-80">Informações do Contrato</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Número do Contrato</span>
                        <span className="bsds-text-regular">{adhesionDetail.contractNumber || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Origem</span>
                        <span className="bsds-text-regular">{adhesionDetail.contractOrigin || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Data de Início</span>
                        <span className="bsds-text-regular">{formatDate(adhesionDetail.ccpStartDate) || '-'}</span>
                      </div>
                      <div>
                        <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Data de Término</span>
                        <span className="bsds-text-regular">{formatDate(adhesionDetail.ccpEndDate) || '-'}</span>
                      </div>
                    </div>
                  </BsdsCard>

                  {/* Action button */}
                  <div className="bsds-display-flex bsds-justify-content-end bsds-mt-16 gap-4">
                    <Button
                      onClick={handleCloseModal}
                      color="secondary"
                      fill="outline"
                      disabled={isLoading}
                      loading={isLoading}
                      className="bsds-width-100"
                    >
                      Fechar
                    </Button>
                    <Button
                      onClick={() => {
                        const request: DownloadAdhesionDetailRequestDTO = {
                          adhesionProposalId: adhesionDetail.adhesionId,
                        };
                        downloadAdhesionDetail(request).then(() => {
                          showMessage('Download iniciado com sucesso', 'success');
                        }).catch((err) => {
                          const errorMsg = err instanceof Error ? err.message : 'Erro ao fazer download da adesão';
                          showMessage(errorMsg, 'error');
                        });
                      }}
                      color="primary"
                      fill="solid"
                      icon={iconDoc2aVia}
                      iconPosition="start"
                      disabled={isLoading}
                      loading={isLoading}
                      className="bsds-width-100"
                    >
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </BsdsModal>
    </div>
  );
};

export default DownloadAdhesionTab;

